import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShareAlt } from "@fortawesome/free-solid-svg-icons";
import FamilySharingModal from "../../alerts/FamilySharingModal";
import ScamReportAPI from "../../services/ScamReportAPI";
import { useSubscription } from "../../context/SubscriptionContext";

/**
 * Share button for threat banners displayed when a scam is detected.
 *
 * This button allows users to share detected scam alerts with family members
 * WITHIN the same KinProtect account only. This is NOT social media sharing —
 * it uses the same in-app family sharing feature from the Alerts Intelligence Hub.
 *
 * @param {boolean} scamDetected - Whether a scam/threat was detected
 * @param {string} threatType - Type of scam (e.g., "Email Scam", "SMS Scam", "Phone Scam")
 * @param {string} threatTitle - Title/summary for the alert
 * @param {string} [threatDetail] - Optional detail about the threat
 * @param {number} [reportId] - ID of the saved scam report (from ScamReportAPI.saveReport)
 */
function ThreatBannerShareButton({
  scamDetected,
  reportId,
  threatType,
  threatTitle,
  threatDetail,
}) {
  const [sharingModalOpen, setSharingModalOpen] = useState(false);
  const { canShare } = useSubscription();
  const hasShareCapability = canShare();

  // Only show when a scam/threat is actually detected
  if (!scamDetected) return null;

  // Build an alert-like object for the FamilySharingModal
  const alertForSharing = {
    id: reportId || Date.now(),
    title: threatTitle || `${threatType || "Scam"} Detected`,
    description:
      threatDetail || `A ${threatType || "scam"} was detected during analysis.`,
    type: threatType || "Scam Detection",
    severity: "HIGH",
    timestamp: new Date().toISOString(),
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    setSharingModalOpen(true);
  };

  const handleShareAlert = async (alertId, options = {}) => {
    if (!reportId) {
      throw new Error("No report available to share. Please try again.");
    }
    try {
      const response = await ScamReportAPI.shareReport(
        reportId,
        options.message || null,
      );
      return response;
    } catch (error) {
      console.error("[ThreatBannerShareButton] Error sharing:", error);
      throw error;
    }
  };

  return (
    <>
      <button
        className="threat-banner-share-btn"
        onClick={hasShareCapability ? handleShareClick : undefined}
        disabled={!hasShareCapability}
        title={
          hasShareCapability
            ? "Share this alert with family members in your account"
            : "Available For Family or Extended Family Plans"
        }
        aria-label={
          hasShareCapability
            ? "Share with family members in your account"
            : "Available For Family or Extended Family Plans"
        }
      >
        <FontAwesomeIcon icon={faShareAlt} className="threat-share-icon" />
        <span className="threat-share-label">Share</span>
      </button>

      {hasShareCapability && (
        <FamilySharingModal
          isOpen={sharingModalOpen}
          alert={alertForSharing}
          onClose={() => setSharingModalOpen(false)}
          onShare={handleShareAlert}
        />
      )}
    </>
  );
}

export default ThreatBannerShareButton;
